import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, Inbox } from 'lucide-react';
import { Button, cn, EmptyState, SidePanel, Skeleton } from '@hrms/ui';
import type { NotificationListItem } from '@hrms/shared';
import { tsr } from '../../lib/api.js';
import { formatRelativeTime } from '../../lib/datetime.js';
import { useNotificationSocket } from '../../lib/socket.js';
import { errorMessage } from '../recruitment/errorMessage.js';

const UNREAD_COUNT_QUERY_KEY = ['notifications', 'unread-count'] as const;
const LIST_QUERY_KEY_ROOT = ['notifications', 'list'] as const;
/** Polling fallback for a realtime connection that never came up, or dropped and gave up quietly
 * (see `lib/socket.tsx`) — the bell still catches up within a minute even with no socket at all.
 * Only while the socket is down: while it is up, `notification:new` already says when to refetch. */
const UNREAD_COUNT_POLL_MS = 60_000;
const PAGE_LIMIT = 20;

/**
 * The topbar bell: a live unread count plus the drawer of the caller's own notifications.
 *
 * Wires up the dead placeholder button `AppShell.tsx` used to render — same markup, same position,
 * now backed by `GET /notifications/unread-count` (polled as a fallback, bumped immediately by the
 * `notification:new` socket event) and a `SidePanel` drawer over `GET /notifications`.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const socket = useNotificationSocket();

  const count = useUnreadNotificationCount();

  useEffect(() => {
    const refresh = () => {
      // Invalidating rather than incrementing local state: the drawer's own list query needs
      // refetching too, and this is the one signal that has to reach both — see the module header
      // for why a second, separately-maintained counter is not worth it here.
      void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY_ROOT });
    };
    const offNotification = socket.onNotification(refresh);
    // Anything that arrived while the socket was down never fired the listener above — and with
    // the socket up there is no polling to catch it (see useUnreadNotificationCount).
    const offReconnect = socket.onReconnect(refresh);
    return () => {
      offNotification();
      offReconnect();
    };
  }, [socket, queryClient]);

  return (
    <>
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setOpen(true)}
        className="relative flex size-10 shrink-0 items-center justify-center rounded-full sm:size-9 bg-bg-subtle text-fg-muted transition-colors hover:text-fg"
      >
        <Bell className="size-4" />
        {count > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold leading-none text-white ring-2 ring-surface"
            aria-hidden="true"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
        <span className="sr-only">
          {count > 0 ? `${count} unread notifications` : 'No unread notifications'}
        </span>
      </button>

      <SidePanel open={open} onClose={() => setOpen(false)} title="Notifications" width={400}>
        <NotificationFeed open={open} hasUnread={count > 0} onNavigated={() => setOpen(false)} />
      </SidePanel>
    </>
  );
}

/**
 * The caller's unread count — shared by the bell's badge and the inbox's Notifications tab. Same
 * query key, so both read one cache entry and one socket-driven invalidation updates both.
 */
export function useUnreadNotificationCount(): number {
  const { isConnected } = useNotificationSocket();
  const unreadCount = tsr.notifications.unreadCount.useQuery({
    queryKey: UNREAD_COUNT_QUERY_KEY,
    // Pushed while the socket is up, polled only while it is down. Coming back to the tab also
    // refetches, which catches anything marked read in another tab (no socket event for that).
    refetchInterval: isConnected ? false : UNREAD_COUNT_POLL_MS,
    refetchOnWindowFocus: true,
  });
  return unreadCount.data?.status === 200 ? unreadCount.data.body.count : 0;
}

/**
 * The caller's notification list with mark-read and paging. Split from `NotificationBell` so its
 * query only ever runs while the drawer is actually open — `open` gates whether this component is
 * mounted at all (see the caller), which both saves a request on every page that renders the shell
 * and gives every re-open a fresh first page.
 *
 * Exported for the unified inbox's Notifications tab, which renders this same feed as a page
 * rather than rebuilding it — one list, one mark-read behaviour, wherever it appears.
 */
export function NotificationFeed({
  open = true,
  hasUnread,
  onNavigated = () => undefined,
}: {
  open?: boolean;
  /** The bell's own unread count, not derived from the (possibly partially loaded) list below —
   * a person with unread items on a page not yet fetched must still see "Mark all as read" enabled. */
  hasUnread: boolean;
  onNavigated?: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];

  const query = { limit: PAGE_LIMIT, ...(cursor ? { cursor } : {}) };
  const list = tsr.notifications.list.useQuery({
    queryKey: [...LIST_QUERY_KEY_ROOT, query],
    queryData: { query },
    enabled: open,
  });

  const [items, setItems] = useState<NotificationListItem[]>([]);
  useEffect(() => {
    const body = list.data?.status === 200 ? list.data.body : null;
    if (!body) return;
    setItems((prev) => (cursor ? [...prev, ...body.items] : body.items));
    // Deliberately keyed on `list.data` alone, not `cursor`: `cursor` is what CAUSED this fetch to
    // return new data, so re-running this effect for a fresh `list.data` value is exactly the
    // condition that should append/replace — re-running it for `cursor` changing on its own (before
    // the corresponding fetch has resolved) would duplicate the still-current page.
  }, [list.data]);

  const nextCursor = list.data?.status === 200 ? list.data.body.nextCursor : null;

  const markRead = tsr.notifications.markRead.useMutation();
  const markAllRead = tsr.notifications.markAllRead.useMutation();

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY_ROOT });
  }

  function handleRowClick(item: NotificationListItem) {
    if (item.readAt) {
      if (item.href) {
        navigate(item.href);
        onNavigated();
      }
      return;
    }
    markRead.mutate(
      { params: { id: item.id } },
      {
        onSuccess: () => {
          invalidate();
          if (item.href) {
            navigate(item.href);
            onNavigated();
          }
        },
      },
    );
  }

  function handleMarkAllRead() {
    markAllRead.mutate({}, { onSuccess: invalidate });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-fg-muted">
          {items.length > 0 ? `${items.length} shown` : 'Your notifications'}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleMarkAllRead}
          loading={markAllRead.isPending}
          disabled={!hasUnread}
        >
          <CheckCheck className="size-3.5" />
          Mark all as read
        </Button>
      </div>

      {markAllRead.isError && (
        <p role="alert" className="rounded-md bg-danger-subtle px-3 py-2 text-xs text-danger">
          {errorMessage(markAllRead.error)}
        </p>
      )}
      {markRead.isError && (
        <p role="alert" className="rounded-md bg-danger-subtle px-3 py-2 text-xs text-danger">
          {errorMessage(markRead.error)}
        </p>
      )}

      {list.isLoading && cursorStack.length === 0 ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : list.isError && items.length === 0 ? (
        <p role="alert" className="rounded-md bg-danger-subtle px-3 py-2 text-xs text-danger">
          {errorMessage(list.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Inbox className="size-8" />}
          title="Nothing here yet"
          description="You'll see approvals, reminders, and updates from every module here as they happen."
        />
      ) : (
        <ul className="flex flex-col gap-1">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => handleRowClick(item)}
                className={cn(
                  'flex w-full flex-col gap-0.5 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-bg-subtle',
                  !item.readAt && 'bg-accent-subtle',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-fg">{item.title ?? 'Notification'}</p>
                  {!item.readAt && (
                    <span
                      className="mt-1 size-1.5 shrink-0 rounded-full bg-accent"
                      aria-hidden="true"
                    />
                  )}
                </div>
                {item.body && <p className="text-xs text-fg-muted">{item.body}</p>}
                <p className="text-[10px] text-fg-subtle">
                  {formatRelativeTime(item.createdAt, new Date())}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {nextCursor && (
        <Button
          variant="secondary"
          size="sm"
          className="self-center"
          loading={list.isFetching}
          onClick={() => setCursorStack((stack) => [...stack, nextCursor])}
        >
          Load more
        </Button>
      )}
    </div>
  );
}
